import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Edit,
  LogOut,
  Mail,
  Phone,
  Building2,
  Stethoscope,
  Clock,
  User,
  MapPin,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/base/Button';
import { Card, CardContent } from '@/components/base/Card';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from 'sonner';
import { logger } from '@/lib/utils/logger';
import { cn } from '@/lib/utils/cn';
import { UserRoleBadge } from '@/components/shared/DynamicNav';

/**
 * ProfileField Component
 * Label-value pair with icon and subtle styling
 */
function ProfileField({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | null;
}) {
  return (
    <div className="flex items-center gap-4 rounded-lg bg-gray-50 px-5 py-4">
      <div className="rounded-md bg-primary/10 p-2.5">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <p
          className={cn('text-base font-medium', value ? 'text-gray-900' : 'italic text-gray-400')}
        >
          {value || 'Not available'}
        </p>
      </div>
    </div>
  );
}

export function DoctorProfilePage() {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const [isOnline, setIsOnline] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  logger.debug('Rendering DoctorProfilePage for user:', user);

  const handleLogout = () => {
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  const handleEditProfile = () => {
    toast.info('Edit profile functionality coming soon');
  };

  const handleToggleStatus = () => {
    setIsOnline(!isOnline);
    toast.success(isOnline ? 'Status changed to Offline' : 'Status changed to Online');
  };

  const getDoctorInitial = (name: string | null) => {
    if (!name) return '?';
    const nameWithoutDr = name.replace(/^Dr\.\s*/, '');
    return nameWithoutDr.charAt(0).toUpperCase() || '?';
  };

  const practitionerData = {
    name:
      user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.username
          ? `${user.username}`
          : null,
    designation: user?.practitionerRole?.specialty || user?.specialization || null,
    department: user?.practitionerRole?.department || null,
    availability: null as string | null,
    email: user?.email || null,
    phone: null as string | null,
    clinicName: user?.projectName || null,
    address: user?.projectName || null,
    avatarUrl: null as string | null,
  };

  return (
    <AppLayout>
      <div className="h-full bg-gradient-to-br from-gray-50 to-gray-100 py-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Left Card - Profile */}
            <div className="lg:col-span-1">
              <Card className="h-full overflow-hidden border-0 shadow-md">
                <CardContent className="flex flex-col p-0">
                  {/* Avatar Section with gradient background */}
                  <div className="relative bg-gradient-to-br from-primary/15 to-primary/5 px-6 py-8">
                    <div className="mx-auto flex h-32 w-32 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-gray-200 shadow-lg">
                      {avatarError || !practitionerData.avatarUrl ? (
                        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#3d4f7c] to-[#2a3a5c] text-white">
                          <span className="text-5xl font-semibold">
                            {getDoctorInitial(practitionerData.name)}
                          </span>
                        </div>
                      ) : (
                        <img
                          src={practitionerData.avatarUrl}
                          alt={practitionerData.name || 'Doctor'}
                          className="h-full w-full object-cover"
                          onError={() => setAvatarError(true)}
                        />
                      )}
                    </div>
                    {/* Status Badge */}
                    <button
                      onClick={handleToggleStatus}
                      className={cn(
                        'absolute right-4 top-4 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium shadow-sm transition-colors',
                        isOnline
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : 'bg-gray-400 text-white hover:bg-gray-500'
                      )}
                    >
                      <span
                        className={cn(
                          'h-1.5 w-1.5 rounded-full',
                          isOnline ? 'animate-pulse bg-white' : 'bg-gray-200'
                        )}
                      />
                      {isOnline ? 'Online' : 'Offline'}
                    </button>
                  </div>

                  {/* Name Section */}
                  <div className="border-b border-gray-100 px-6 py-5 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <h2 className="font-serif text-3xl font-medium leading-h4 tracking-tight text-pranik-brand-700">
                        {practitionerData.name || 'Doctor'}
                      </h2>
                      <UserRoleBadge />
                    </div>
                    {practitionerData.designation && (
                      <p className="mt-1.5 font-hanken text-lg font-normal leading-label-2 tracking-label-2 text-gray-700">
                        {practitionerData.designation}
                      </p>
                    )}
                    {practitionerData.department && (
                      <p className="text-sm text-gray-500">{practitionerData.department}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 p-5">
                    <Button
                      onClick={handleEditProfile}
                      variant="outline"
                      className="w-full justify-center gap-2 border-primary/30 text-primary hover:bg-primary/5"
                    >
                      <Edit className="h-4 w-4" />
                      Edit Profile
                    </Button>
                    <Button
                      onClick={handleLogout}
                      variant="ghost"
                      className="w-full justify-center gap-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Card - Details */}
            <div className="lg:col-span-2">
              <Card className="h-full border-0 shadow-md">
                <CardContent className="space-y-8 p-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                      <User className="h-5 w-5 text-primary" />
                      Personal Information
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <ProfileField icon={User} label="Full Name" value={practitionerData.name} />
                      <ProfileField
                        icon={Stethoscope}
                        label="Specialization"
                        value={practitionerData.designation}
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                      <Mail className="h-5 w-5 text-primary" />
                      Contact Information
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <ProfileField
                        icon={Mail}
                        label="Email Address"
                        value={practitionerData.email}
                      />
                      <ProfileField
                        icon={Phone}
                        label="Phone Number"
                        value={practitionerData.phone}
                      />
                      <ProfileField
                        icon={Clock}
                        label="Availability"
                        value={practitionerData.availability}
                      />
                    </div>
                  </div>

                  {/* Clinic Information */}
                  <div>
                    <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                      <Building2 className="h-5 w-5 text-primary" />
                      Clinic / Hospital
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <ProfileField
                        icon={Building2}
                        label="Clinic / Hospital Name"
                        value={practitionerData.clinicName}
                      />
                      <ProfileField
                        icon={MapPin}
                        label="Location"
                        value={practitionerData.address}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default DoctorProfilePage;
